import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import type { KibSuperSelectOption } from '@chewy/kib-fields-new-react'
import { KibField, KibSuperSelect } from '@chewy/kib-fields-new-react'
import { KibCopyIcon, KibDeleteFillIcon } from '@chewy/kib-icons-react'

import RhapKibDatePicker from '~/components/common/inputs/kib-date-picker/RhapKibDatePicker'
import { getInventoryOrdersIsUpdating } from '~/store/duck/inventoryOrders'
import { getInventoryDamageReasons } from '~/store/reducers/constants'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import { getOneUomPluralized } from '../../inventoryUtils'
import type { ReceiptFields } from './hooks/useReceiptFields'
import { OrderItemReceiptNotes } from './OrderItemReceiptNotes'
import { QuantityAutocompleteField } from './QuantityAutocompleteField'

import '@chewy/kib-datepicker-styles/dist/kib-datepicker.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-super-select.css'
import styles from './OrderItemReceipt.module.scss'

interface OrderItemReceiptProps {
  canDelete?: boolean
  canDuplicate?: boolean
  fields: ReceiptFields
  isDraft?: boolean
  isViewOnly?: boolean
  onDelete?: () => void
  onDuplicate?: () => void
  orderedQuantity: number
  packageTypeName: string
  perPackageAmount: number
  productSizeUnit: string
}

export const OrderItemReceipt = ({
  canDelete = false,
  canDuplicate = false,
  fields,
  isDraft = false,
  isViewOnly = false,
  onDelete,
  onDuplicate,
  orderedQuantity,
  packageTypeName,
  perPackageAmount,
  productSizeUnit,
}: OrderItemReceiptProps) => {
  const { t } = useTranslation('Admin')
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const InventoryDamageReasons = useSelector(getInventoryDamageReasons)
  const timezone = useCurrentBusinessTimezone()

  const {
    receivedQuantity,
    lotNumber,
    expirationDate,
    serialNumber,
    businessReferenceNumber,
    damaged,
    damageReasonId,
    damageNotes,
  } = fields

  const RECEIVE_ACTION_VALUE = 'receive'

  const actionOptions: KibSuperSelectOption[] = [
    {
      label: t('Admin:CATALOG.ORDERS.RECEIVE_ACTION'),
      value: RECEIVE_ACTION_VALUE,
    },
    ...InventoryDamageReasons.map((reason) => ({
      label: t('Admin:CATALOG.ORDERS.REJECT_REASON_ACTION', {
        reason: reason.name,
      }),
      value: reason.id,
    })),
  ]

  const selectedActionValue = damaged.value
    ? damageReasonId.value
    : RECEIVE_ACTION_VALUE

  const selectedAction =
    actionOptions.find((opt) => opt.value === selectedActionValue) ??
    actionOptions[0]

  const handleActionChange = (selected: KibSuperSelectOption[]) => {
    const selectedValue = selected[0]?.value
    if (!selectedValue || selectedValue === RECEIVE_ACTION_VALUE) {
      damaged.setValue(false)
      damageReasonId.setValue('')
    } else {
      damaged.setValue(true)
      damageReasonId.setValue(selectedValue)
    }
  }

  const inputDisabled = !isDraft || isUpdating || isViewOnly

  const receiptQuantity = receivedQuantity.value ?? 1
  const productSizeUnitLabel = getOneUomPluralized(
    productSizeUnit,
    receiptQuantity,
  )

  return (
    <div className={styles.container}>
      <div className={styles.fieldsRow}>
        <div className={styles.quantityWrapper}>
          <QuantityAutocompleteField
            autoFocus
            required
            disabled={inputDisabled}
            field={receivedQuantity}
            invalid={receivedQuantity.errors.length > 0}
            label={receivedQuantity.label!}
            messagesContent={receivedQuantity.message}
            orderedQuantity={orderedQuantity}
            packageTypeName={packageTypeName}
            perPackageAmount={perPackageAmount}
            productSizeUnit={productSizeUnit}
          />
          <span className={styles.quantityLabel}>{productSizeUnitLabel}</span>
        </div>

        <KibSuperSelect
          disabled={inputDisabled}
          label={t('Admin:CATALOG.ORDERS.ACTION_LABEL')}
          options={actionOptions}
          value={[selectedAction]}
          onChange={handleActionChange}
        />

        <KibField
          disabled={inputDisabled}
          label={lotNumber.label!}
          value={lotNumber.value ?? ''}
          onChange={(e) => lotNumber.setValue(e.target.value)}
        />

        <KibField
          disabled={inputDisabled}
          label={serialNumber.label!}
          value={serialNumber.value ?? ''}
          onChange={(e) => serialNumber.setValue(e.target.value)}
        />

        <RhapKibDatePicker
          disabled={inputDisabled}
          label={expirationDate.label}
          renderInputNew={(props) => <KibField {...props} />}
          timezone={timezone}
          value={expirationDate.value}
          onChange={(date) => expirationDate.setValue(date)}
        />

        <KibField
          disabled={inputDisabled}
          label={businessReferenceNumber.label!}
          value={businessReferenceNumber.value ?? ''}
          onChange={(e) => businessReferenceNumber.setValue(e.target.value)}
        />
        {!isViewOnly && (
          <div className={styles.actionButtonContainer}>
            <KibButtonNew
              iconOnly
              className={styles.actionButton}
              disabled={!canDuplicate}
              emphasis="tertiary"
              icon={<KibCopyIcon />}
              onClick={onDuplicate}
            >
              {t('Common:DUPLICATE')}
            </KibButtonNew>
            <KibButtonNew
              iconOnly
              className={styles.actionButton}
              disabled={inputDisabled || !canDelete}
              emphasis="tertiary"
              icon={<KibDeleteFillIcon />}
              onClick={onDelete}
            >
              {t('Common:DELETE_ROW')}
            </KibButtonNew>
          </div>
        )}
      </div>
      <div className={styles.notesWrapper}>
        <OrderItemReceiptNotes
          damageNotesField={damageNotes}
          isDamaged={damaged.value}
          isDisabled={inputDisabled}
          isDraft={isDraft}
        />
      </div>
    </div>
  )
}
